/**
 * RealEstateSearchProfileForm — Créer/éditer un profil de recherche immobilière
 *
 * Inspiré de CreateOpportunityForm (pattern trigger + initialValues + onCreated/onError).
 * Permet à l'utilisateur de configurer ses critères de recherche par pays/localisation,
 * qui déclenchent ensuite l'import automatique d'offres immobilières personnalisées.
 */

import React, { useState } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
} from '@mui/material'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import { useForm, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Input } from 'Components/Input'
import { makeGetRequest, makePostRequest, makePatchRequest } from 'utils/api'

export default function RealEstateSearchProfileForm({
  initialValues,
  trigger,
  onCreated,
  onError,
  profileId,
}) {
  const { t } = useTranslation('layout')
  const [open, setOpen] = useState(false)
  const [feedback, setFeedback] = useState(null) // 'success' | 'error' | null
  const [isEditing, setIsEditing] = useState(!!profileId)

  const emptyDefaults = {
    label: '',
    country: 'FR',
    localisation: '',
    rayon_km: null,
    prix_min: null,
    prix_max: null,
    type_bien: '',
    surface_min: null,
    pieces_min: null,
    rendement_min: null,
    actif: true,
  }

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: { ...emptyDefaults, ...initialValues },
  })

  // LAP — `initialValues`/`profileId` sont chargés de façon asynchrone par le
  // parent (RealEstateCard récupère le profil existant après son propre
  // montage) : au premier rendu de ce formulaire, ils sont encore `undefined`.
  // `useForm({ defaultValues })` et `useState(!!profileId)` ne sont évalués
  // QU'UNE FOIS au montage, donc le formulaire restait bloqué sur "création
  // vide" même une fois le profil chargé — en rouvrant le popup, les critères
  // précédemment enregistrés n'apparaissaient jamais. On resynchronise donc
  // explicitement le formulaire sur les props courantes à CHAQUE ouverture.
  const handleClickOpen = () => {
    setIsEditing(!!profileId)
    reset({ ...emptyDefaults, ...initialValues })
    setOpen(true)
  }

  const handleClose = () => {
    reset()
    setOpen(false)
    setIsEditing(false)
  }

  // Les <Select> (Pays, Type de bien) rendent leur menu dans un Portal attaché
  // à document.body, en dehors du DOM de la Dialog. Au clic sur une option, le
  // menu se démonte avant que le mouseup ne remonte : MUI ne retrouve plus le
  // point de clic à l'intérieur du noeud de la Dialog et interprète ça comme
  // un clic sur le backdrop, ce qui ferme la popup (dès le premier clic dans
  // un champ Select, avant même d'avoir choisi une valeur). On ignore donc
  // explicitement les fermetures de type "backdropClick" : seuls le bouton
  // Annuler, la soumission réussie (onSubmit → handleClose) et la touche
  // Echap doivent fermer la popup.
  const handleDialogClose = (event, reason) => {
    if (reason === 'backdropClick') return
    handleClose()
  }

  const onSubmit = async (data) => {
    const payload = {
      label: data.label,
      country: data.country,
      localisation: data.localisation,
      rayon_km: data.rayon_km ? parseInt(data.rayon_km) : null,
      prix_min: data.prix_min ? parseFloat(data.prix_min) : null,
      prix_max: data.prix_max ? parseFloat(data.prix_max) : null,
      type_bien: data.type_bien || null,
      surface_min: data.surface_min ? parseFloat(data.surface_min) : null,
      pieces_min: data.pieces_min ? parseFloat(data.pieces_min) : null,
      rendement_min: data.rendement_min ? parseFloat(data.rendement_min) : null,
      actif: data.actif !== false,
    }

    let response
    if (isEditing && profileId) {
      // Modification
      response = await makePatchRequest(
        `/api/investment/real-estate-search-profiles/${profileId}/`,
        payload,
        {},
        true
      )
    } else {
      // Création
      response = await makePostRequest(
        '/api/investment/real-estate-search-profiles/',
        payload,
        {},
        true
      )
    }

    if (response.status === (isEditing ? 200 : 201)) {
      setFeedback('success')
      if (typeof onCreated === 'function') onCreated(response.data)
      handleClose()
    } else {
      setFeedback('error')
      if (typeof onError === 'function') onError(response)
    }
  }

  return (
    <>
      {trigger ? (
        React.cloneElement(trigger, {
          onClick: (event) => {
            if (typeof trigger.props.onClick === 'function')
              trigger.props.onClick(event)
            handleClickOpen()
          },
        })
      ) : (
        <Button
          variant="outlined"
          startIcon={<SettingsOutlinedIcon />}
          onClick={handleClickOpen}
        >
          {t('investments.markeet.realEstate.configSearch')}
        </Button>
      )}

      <Dialog fullWidth maxWidth="sm" open={open} onClose={handleDialogClose}>
        <DialogTitle sx={{ color: 'Black !important' }}>
          {isEditing
            ? t('investments.forms.realEstate.editTitle')
            : t('investments.forms.realEstate.createTitle')}
        </DialogTitle>
        <form
          onSubmit={handleSubmit(onSubmit)}
          // LAP — les champs requis (label, localisation) portent l'attribut
          // HTML natif `required` (via inputProps). Sans `noValidate`, le
          // navigateur bloque la soumission AVANT react-hook-form et tente
          // d'afficher sa bulle de validation natif — bulle qui est rognée/
          // invisible ici car DialogContent est en `overflow-y: auto` : au
          // clic sur "Créer", l'utilisateur ne voyait alors rien se passer,
          // sans aucune requête réseau ni message d'erreur. On délègue donc
          // entièrement la validation à react-hook-form (cf. Login.js, même
          // pattern `noValidate`), qui affiche maintenant un vrai message
          // d'erreur sous le champ (cf. correctif Components/Input).
          noValidate
          onKeyDown={(event) => {
            // La touche Entrée dans un champ (label, rayon, prix...) soumettait
            // le formulaire dès que les deux champs obligatoires étaient valides,
            // fermant le popup avant que l'utilisateur ait fini de renseigner ses
            // critères. On la neutralise sur les champs texte/nombre ; la
            // validation reste accessible via le bouton "Créer"/"Modifier".
            if (event.key === 'Enter' && event.target.tagName === 'INPUT') {
              event.preventDefault()
            }
          }}
        >
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Label / Nom de la recherche */}
            <Input
              control={control}
              label={t('investments.forms.realEstate.label')}
              inputProps={{
                variant: 'outlined',
                required: true,
                size: 'small',
                type: 'text',
                placeholder: 'ex: Studio Genève',
              }}
              name="label"
              rules={{ required: true }}
              errors={errors}
            />

            {/* Pays */}
            <Controller
              name="country"
              control={control}
              render={({ field }) => (
                <FormControl size="small">
                  <InputLabel>{t('investments.forms.realEstate.country')}</InputLabel>
                  <Select
                    {...field}
                    label={t('investments.forms.realEstate.country')}
                  >
                    <MenuItem value="FR">France</MenuItem>
                    <MenuItem value="CH">Suisse</MenuItem>
                  </Select>
                </FormControl>
              )}
            />

            {/* Localisation */}
            <Input
              control={control}
              label={t('investments.forms.realEstate.localisation')}
              inputProps={{
                variant: 'outlined',
                required: true,
                size: 'small',
                type: 'text',
                placeholder: 'ex: Genève',
              }}
              name="localisation"
              rules={{ required: true }}
              errors={errors}
            />

            {/* Rayon km */}
            <Input
              control={control}
              label={t('investments.forms.realEstate.rayonKm')}
              inputProps={{
                variant: 'outlined',
                size: 'small',
                type: 'number',
                placeholder: '10',
              }}
              name="rayon_km"
              errors={errors}
            />

            {/* Prix min/max */}
            <div style={{ display: 'flex', gap: 12 }}>
              <Input
                control={control}
                label={t('investments.forms.realEstate.prixMin')}
                inputProps={{
                  variant: 'outlined',
                  size: 'small',
                  type: 'number',
                  placeholder: '100000',
                  style: { flex: 1 },
                }}
                name="prix_min"
                errors={errors}
              />
              <Input
                control={control}
                label={t('investments.forms.realEstate.prixMax')}
                inputProps={{
                  variant: 'outlined',
                  size: 'small',
                  type: 'number',
                  placeholder: '500000',
                  style: { flex: 1 },
                }}
                name="prix_max"
                errors={errors}
              />
            </div>

            {/* Type de bien */}
            <Controller
              name="type_bien"
              control={control}
              render={({ field }) => (
                <FormControl size="small">
                  <InputLabel>{t('investments.forms.realEstate.typeBien')}</InputLabel>
                  <Select
                    {...field}
                    label={t('investments.forms.realEstate.typeBien')}
                  >
                    <MenuItem value="">Tous</MenuItem>
                    <MenuItem value="appartement">Appartement</MenuItem>
                    <MenuItem value="maison">Maison</MenuItem>
                    <MenuItem value="terrain">Terrain</MenuItem>
                    <MenuItem value="parking">Parking</MenuItem>
                    <MenuItem value="autre">Autre</MenuItem>
                  </Select>
                </FormControl>
              )}
            />

            {/* Surface min / Pièces min / Rendement min */}
            <div style={{ display: 'flex', gap: 12 }}>
              <Input
                control={control}
                label={t('investments.forms.realEstate.surfaceMin')}
                inputProps={{
                  variant: 'outlined',
                  size: 'small',
                  type: 'number',
                  placeholder: '50',
                  style: { flex: 1 },
                }}
                name="surface_min"
                errors={errors}
              />
              <Input
                control={control}
                label={t('investments.forms.realEstate.piecesMin')}
                inputProps={{
                  variant: 'outlined',
                  size: 'small',
                  type: 'number',
                  placeholder: '2',
                  style: { flex: 1 },
                }}
                name="pieces_min"
                errors={errors}
              />
            </div>

            {/* Rendement min */}
            <Input
              control={control}
              label={t('investments.forms.realEstate.rendementMin')}
              inputProps={{
                variant: 'outlined',
                size: 'small',
                type: 'number',
                placeholder: '4.5',
              }}
              name="rendement_min"
              errors={errors}
            />
          </DialogContent>

          <DialogActions>
            <Button onClick={handleClose}>
              {t('investments.forms.realEstate.cancelButton')}
            </Button>
            <Button type="submit" variant="contained" color="primary">
              {isEditing
                ? t('investments.forms.realEstate.updateButton')
                : t('investments.forms.realEstate.createButton')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Feedback Snackbar */}
      <Snackbar
        open={!!feedback}
        autoHideDuration={3000}
        onClose={() => setFeedback(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setFeedback(null)}
          severity={feedback === 'error' ? 'error' : 'success'}
          sx={{ width: '100%' }}
        >
          {feedback === 'error'
            ? t('investments.forms.realEstate.error')
            : t('investments.forms.realEstate.success')}
        </Alert>
      </Snackbar>
    </>
  )
}
